/* eslint-disable max-lines */
import { useRouter } from 'next/navigation';

import type { MouseEvent} from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import classNames from 'classnames';
import DOMPurify from 'isomorphic-dompurify';
import { useDisconnect } from 'wagmi';

import ImageProxy from '@next-image-proxy';

import { Card, HorizontalSeparator, useModalController } from '@uikit/index';

import { MIXPANEL_EVENT_NAMES } from '@shared/api/mixPanel';
import type { BinanceCheckoutEntry, DeserializedHotOffer } from '@shared/api/types/hotOffers';
import KarmaTag from '@shared/common/components/KarmaTag';
import { AuthWeb3Modal } from '@shared/common/components/modals/AuthWeb3Modal';
import { CompleteYourMagicIdModal } from '@shared/common/components/modals/CompleteYourMagicIdModal';
import {
  getMembershipClassNames,
  getMembershipName,
  isAtLeastMembership,
} from '@shared/common/components/NavLayout/utils';
import ToastLink from '@shared/common/components/ToastLink';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useMixPanel } from '@shared/common/providers/MixPanelProvider';
import { useUserAgentDetect } from '@shared/common/providers/UserAgentDetectProvider';
import { HOT_OFFER_TYPE } from '@shared/constants';
import { nextProjectUrl } from '@shared/constants/urls';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import { openLinkByTag } from '@shared/helpers/openLinkByTag';
import clearSavedConnectionDetails from '@shared/helpers/web3/clearSavedConnectionDetails';

import { useFeatureTogglesQuery } from '@query/featureToggles';
import { useCanClaimOfferQuery, useClaimOfferUrlQuery, useCreateBinancePayMutation } from '@query/hotOffer';

import { Button } from '@ui/uikit/components/common/Button';
import Membership from '@ui/uikit/components/icons/mono/Membership';

import AccessModal from '../../../pages/HotOfferPage/AccessModal';
import { UnsupportedBrowserModal } from '../../../pages/HotOfferPage/HotOfferCard/UnsupportedBrowserModal';
import HotOfferShareButton from '../../../pages/HotOffersPage/HotOfferShareButton';
import HotOfferStats, { USDT_SQR_EXCH_CURRENCY } from '../../../pages/HotOffersPage/HotOfferStats';
import { CardConfig, VerificationStatus } from '../../../pages/MagicId/shared/ui/VerificationCard';
import HotOfferCheckoutModal from '../HotOfferCheckoutModal';
import HotOfferClaimButton from '../HotOfferClaimButton';
import HotOfferFailModal from '../HotOfferFailModal';
import HotOfferSuccessModal from '../HotOfferSuccessModal';
import KarmaLimitModal from '../KarmaLimitModal';
import { InfoRow } from './InfoRow';

export interface HotOfferCardProps {
  offer: DeserializedHotOffer;
  status?: string;
}

const supportedBrowsers = ['Chrome', 'Firefox', 'Safari', 'Edge', 'Brave', 'Opera', 'Yandex'];

export default function HotOfferCard({ offer: offerFromProps, status }: HotOfferCardProps) {
  const [isOpenMagicIDModal, setIsOpenMagicIDModal] = useState(false);
  const [isOpenEVMModal, setIsOpenEVMModal] = useState(false);
  const [redirectLink, setRedirectLink] = useState<string>('');

  const {
    id = '',
    type = HOT_OFFER_TYPE.OFFER,
    attributes: {
      icon = '',
      name = 'Unnamed',
      offerAmount = '0',
      offerCurrency = 'USDT',
      cashBackPercentage = 0,
      offer = '',
      claimUrl,
      lockPeriod = 1,
      claimKarmaThreshold = 0,
      accessTier = 'FREE',
      humanityCheckRequired,
    } = {},
  } = offerFromProps;
  const karmaLimitModal = useModalController();
  const checkoutModal = useModalController();
  const failModal = useModalController(status === 'fail');
  const successModal = useModalController(status === 'success');
  const accessModal = useModalController();
  const { browserName } = useUserAgentDetect();

  const [binanceCheckoutEntry, setBinanceCheckoutEntry] = useState<
    BinanceCheckoutEntry | undefined
  >();
  const [accessErrorTime, setAccessErrorTime] = useState<number>(0);

  const { disconnectAsync } = useDisconnect();
  const { user, isAuth } = useAuth();
  const { track } = useMixPanel();

  const [isUnsupportedBrowserModalOpen, setUnsupportedBrowserModalOpen] = useState(false);

  const isSupportedBrowser = supportedBrowsers.includes(browserName);
  const handleCloseUnsupportedBrowserModal = () => setUnsupportedBrowserModalOpen(false);

  const trackHotOfferClaimClick = useCallback(() => {
    track(MIXPANEL_EVENT_NAMES.HOT_OFFER_CLAIM_BUTTON_CLICK, {
      distinct_id: user?.id,
      offer: name,
    });
  }, [name, track, user?.id]);

  const { data: { binanceHotOffers, hotOffersSharing, membership } = {} } =
    useFeatureTogglesQuery();
  const {
    data: canClaimOffer,
    isFetching: isCanClaimOfferLoading,
    error: canClaimOfferError,
    refetch: refetchCanClaimOffer,
  } = useCanClaimOfferQuery(id, {
    enabled: isAuth && Boolean(id),
  });
  const { isFetching: isClaimOfferUrlLoading, refetch: claimOfferUrl } = useClaimOfferUrlQuery(id, {
    enabled: false,
  });

  const { mutateAsync: createBinancePay, isPending: isCreatingBinancePay } =
    useCreateBinancePayMutation();

  const verificationStatus = user?.attributes.faceCheckPassed
    ? VerificationStatus.VERIFIED
    : VerificationStatus.NOT_VERIFIED;

  const isNotVerified =
    humanityCheckRequired && verificationStatus === VerificationStatus.NOT_VERIFIED;
  const userMembershipTier = user?.membershipTier.attributes.key;
  const needsMembershipUpgrade = userMembershipTier && !isAtLeastMembership(userMembershipTier, accessTier);

  const isDisableClaim = useMemo(() => {
    if (isAuth && canClaimOffer?.attributes?.claimed) {
      return true;
    }

    if (
      isAuth &&
      !canClaimOffer?.attributes?.canBeClaimed &&
      canClaimOffer?.attributes.noUserData
    ) {
      return true;
    }

    if (isAuth && (isNotVerified || needsMembershipUpgrade)) {
      return true;
    }

    return false;
  }, [canClaimOffer?.attributes?.canBeClaimed, canClaimOffer?.attributes?.claimed, canClaimOffer?.attributes.noUserData, isAuth, isNotVerified, needsMembershipUpgrade]);

  const updateModalState = useCallback(
    async (isOpen: boolean) => {
      clearSavedConnectionDetails();

      await disconnectAsync();

      setIsOpenMagicIDModal(isOpen);

      if (!isOpen) {
        refetchCanClaimOffer();
      }
    },
    [disconnectAsync, refetchCanClaimOffer],
  );

  const getIsUserAbleToClaimOffer = useCallback(() => {
    if (!isAuth) {
      setIsOpenEVMModal(true);

      return false;
    }

    if (canClaimOfferError) {
      displayErrorToast({ error: canClaimOfferError });

      refetchCanClaimOffer();

      return false;
    }

    if (!canClaimOffer?.attributes?.canBeClaimed) {
      const {
        karmaTooLow,
        poolExhausted,
        limitReached,
        suspended,
        claimed,
        noUserData,
        membershipTooLow,
        waitForAccess,
        userTired,
      } = canClaimOffer?.attributes || {};

      if (userTired) {
        toast.info(
          ({ toastProps }) => (
            <div>
              Looks like you’ve claimed this one. Don’t worry — there are plenty of others waiting
              for you. Check the link below.
              <ToastLink
                href={nextProjectUrl.store('/hot-offers?mode=unclaimed')}
                rel="noreferrer "
                target="_blank"
                variant="ghost"
                closeOnClick={false}
                toastId={toastProps.toastId}
                className="text-neutral-50 font-normal"
              >
                View Hot Offers
              </ToastLink>
            </div>
          ),
          {
            autoClose: 60_000,
            closeOnClick: false,
          },
        );
      } else if (noUserData) {
        updateModalState(true);

        return false;
      } else if (claimed) {
        if (type === HOT_OFFER_TYPE.SO_QUEST_OFFER) {
          return true;
        }

        toast.success('You already claimed this offer', { toastId: 'hot-offer-claim-success' });
      } else if (karmaTooLow) {
        karmaLimitModal.open();
      } else if (poolExhausted) {
        toast.error('Sorry, Hot Offer limit is reached. Please claim another one.');
      } else if (limitReached) {
        toast.error(
          'Offer Unavailable! Sorry, this offer has been fully claimed. Please check out our other exciting offers or try again later.',
        );
      } else if (suspended) {
        toast.error('This offer is not available');
      } else if (membershipTooLow || !!waitForAccess) {
        if (!membership) {
          return false;
        }

        // if 0 then will open low access modal otherwise early access modal
        setAccessErrorTime(membershipTooLow ? 0 : waitForAccess || 0);
        accessModal.open();
      }

      return false;
    }

    return true;
  }, [
    accessModal,
    canClaimOffer?.attributes,
    canClaimOfferError,
    isAuth,
    karmaLimitModal,
    membership,
    refetchCanClaimOffer,
    type,
    updateModalState,
  ]);

  const handleClaimOfferButtonClick = useCallback(
    async (event?: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      trackHotOfferClaimClick();

      const isUserAbleToClaimOffer = getIsUserAbleToClaimOffer();

      if (!isUserAbleToClaimOffer) {
        event?.preventDefault();

        return;
      }

      if (!canClaimOffer?.attributes.claimUrl) {
        try {
          const { status, data, error } = (await claimOfferUrl()) ?? {};

          if (status === 'error') {
            displayErrorToast({ error });
            return;
          }

          const link =
            type === HOT_OFFER_TYPE.SO_QUEST_OFFER ? claimUrl ?? '' : data?.attributes.url ?? '';
          setRedirectLink(link);

          if (isSupportedBrowser) {
            openLinkByTag({ link });
          } else {
            setUnsupportedBrowserModalOpen(true);
          }
        } catch (error) {
          displayErrorToast({ error });
        }
      } else {
        try {
          const { status, error } = await claimOfferUrl();

          if (status === 'error') {
            displayErrorToast({ error });
            return;
          }
        } catch (error) {
          displayErrorToast({ error });
        }
      }
    },
    [
      canClaimOffer?.attributes.claimUrl,
      claimOfferUrl,
      claimUrl,
      getIsUserAbleToClaimOffer,
      isSupportedBrowser,
      trackHotOfferClaimClick,
      type,
    ],
  );

  useEffect(() => {
    if (
      !isDisableClaim &&
      canClaimOffer?.attributes.payoutConfig.directReward &&
      !canClaimOffer?.attributes?.claimed &&
      Boolean(user?.isNewUser)
    ) {
      handleClaimOfferButtonClick();
    }
  }, [
    isAuth,
    canClaimOffer?.attributes.payoutConfig.directReward,
    isDisableClaim,
    canClaimOffer?.attributes?.claimed,
    user?.isNewUser,
    handleClaimOfferButtonClick,
  ]);

  const handleClaimBinanceHotOfferButtonClick = async () => {
    trackHotOfferClaimClick();

    const isUserAbleToClaimOffer = getIsUserAbleToClaimOffer();

    if (!isUserAbleToClaimOffer) {
      return;
    }

    try {
      const binancePayEntry = await createBinancePay(id);
      setBinanceCheckoutEntry(binancePayEntry);

      successModal.close();
      failModal.close();
      checkoutModal.open();
    } catch (error) {
      displayErrorToast({
        error,
        replaceErrorMessageCallback: (errorMessage) => {
          if (errorMessage.includes('HotOfferPoolExhaustedError')) {
            return { newMessage: 'Sorry, Hot Offer limit is reached. Please claim another one.' };
          }

          return { newMessage: errorMessage };
        },
      });
    }
  };

  const onSubmit = () => {
    updateModalState(false);
  };

  const pausedText = offerFromProps.attributes.paused;

  const router = useRouter();

  const handleUpgradeClick = () => {
    router.push(nextProjectUrl.store('/profile/membership/plans'));
  };

  const handleVerifyClick = () => {
    router.push(nextProjectUrl.store('/profile/magic-id/human-verification'));
  };

  const formatOfferAmount = () => {
    if (type === HOT_OFFER_TYPE.BINANCE) {
      return `${cashBackPercentage}%/Locked for ${lockPeriod} Days`;
    }

    if (offerCurrency === USDT_SQR_EXCH_CURRENCY) {
      return `$${offerAmount} in SQR`;
    }

    return `${offerAmount} ${offerCurrency}`;
  };

  return (
    <Card className="w-full md:max-w-[472px] xl:max-w-[548px] h-fit flex flex-col gap-4 rounded-[24px] overflow-hidden p-6 bg-neutral-50">
      <div className="w-full flex flex-basis-0 gap-5 items-center">
        <ImageProxy
          className="w-[50px] aspect-square rounded-full border border-primary-200 flex-shrink-0"
          src={icon}
          alt={name}
          width={200}
          height={200}
          data-qa="hot-offer-icon"
        />

        <h1 className="t-h4 leading-6 line-clamp-2 break-words lg:text-xl" data-qa="hot-offer-name">
          {name}
        </h1>
      </div>

      <HorizontalSeparator lineClassName="!bg-neutral-100" />

      <InfoRow
        title="Offer Details"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(offer) }}
      />

      <InfoRow
        title="Offer"
        rightColContent={
          <h2 className="text-neutral-600 leading-[1.7rem]" data-qa="hot-offer-amount">
            {formatOfferAmount()}
          </h2>
        }
      />

      <InfoRow
        title="Min. Karma"
        rightColContent={
          <div className="flex items-center justify-center gap-1 text-secondary-700">
            <KarmaTag points={claimKarmaThreshold} />
            Karma
          </div>
        }
      />

      {humanityCheckRequired && (
        <InfoRow
          title="Liveness Verification"
          details={
            <div className={classNames('inline-flex items-center gap-2')}>
              {CardConfig[verificationStatus]?.icon}
              {`${isNotVerified ? 'Not' : ''}  Verified`}
            </div>
          }
          rightColContent={
            isAuth &&
            isNotVerified && (
              <Button size="medium" width={100} onClick={handleVerifyClick}>
                Verify
              </Button>
            )
          }
        />
      )}

      {Boolean(membership) && (
        <InfoRow
          title="Min. Membership"
          details={
            <div className={classNames('inline-flex items-center gap-2')}>
              <Membership
                className={classNames('size-4', getMembershipClassNames(accessTier).text)}
              />
              {getMembershipName(accessTier)}
            </div>
          }
          rightColContent={
            needsMembershipUpgrade && (
              <Button size="medium" width={100} onClick={handleUpgradeClick}>
                Upgrade
              </Button>
            )
          }
        />
      )}

      <HotOfferStats offer={offerFromProps} className="py-3" />

      <div className="flex items-center justify-end gap-2">
        {([HOT_OFFER_TYPE.OFFER, HOT_OFFER_TYPE.SO_QUEST_OFFER] as string[]).includes(type) && (
          <HotOfferClaimButton
            isLoading={isClaimOfferUrlLoading}
            isCanClaimOfferLoading={isCanClaimOfferLoading}
            canClaimOffer={canClaimOffer}
            onClick={handleClaimOfferButtonClick}
            data-qa="hot-offer-claim-button"
            disabledVisually={isDisableClaim}
            pausedText={pausedText}
          />
        )}

        {type === HOT_OFFER_TYPE.BINANCE && (
          <HotOfferClaimButton
            isLoading={isCreatingBinancePay}
            isCanClaimOfferLoading={isCanClaimOfferLoading}
            canClaimOffer={canClaimOffer}
            onClick={handleClaimBinanceHotOfferButtonClick}
            disabledVisually={isDisableClaim}
            pausedText={pausedText}
          />
        )}

        {hotOffersSharing && !pausedText && <HotOfferShareButton offer={offerFromProps} />}

        <KarmaLimitModal karmaPoints={claimKarmaThreshold} modalController={karmaLimitModal} />

        {type === HOT_OFFER_TYPE.BINANCE && binanceHotOffers && (
          <>
            <HotOfferCheckoutModal
              modalController={checkoutModal}
              offer={offerFromProps}
              binanceCheckoutEntry={binanceCheckoutEntry}
            />

            <HotOfferSuccessModal modalController={successModal} />

            <HotOfferFailModal
              modalController={failModal}
              claimUrl={canClaimOffer?.attributes.claimUrl}
              onRetry={handleClaimOfferButtonClick}
            />
          </>
        )}
      </div>
      <CompleteYourMagicIdModal
        isOpenMagicIDModal={isOpenMagicIDModal}
        hideInputFields={Boolean(canClaimOffer?.attributes.payoutConfig.directReward)}
        formIsDisabled={isClaimOfferUrlLoading}
        onSubmit={onSubmit}
        updateModalState={updateModalState}
      />

      <AuthWeb3Modal isOpen={isOpenEVMModal} setIsOpen={setIsOpenEVMModal} />

      <AccessModal
        {...accessModal.props}
        accessTier={accessTier}
        accessErrorTime={accessErrorTime}
        target="_blank"
      />

      <UnsupportedBrowserModal
        link={redirectLink}
        isOpen={isUnsupportedBrowserModalOpen}
        onClose={handleCloseUnsupportedBrowserModal}
      />
    </Card>
  );
}
